import { isEqual } from 'lodash/fp';
import { lt, useFeatureFlag } from '@mews/framework';
import { useDialogForm } from '@mews-commander/form';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { DropdownItem, IconName } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { FeatureFlag, RouteName } from '@mews-commander/enums';
import { isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { BILLING_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { refetchBill } from '../../../../ducks/bills';
import { PaymentLinkReservationDialogContent } from '../../../../../payment/components/forms/PaymentLink/PaymentLinkReservationDialogContent';
import { openModal } from '../../../../../app';
import { OrderItemDropdownButton } from '../../ItemsGrouping/OrderItemListItemText/OrderItemIconMenu';
import { fetchLinkedReservationOwners } from '../../../../utils/payments';
import { useInvalidatePaymentsTableRecordsQueries } from '../../../../../payment/queries';
import { getAmountDueForBillIds } from '../../../../selectors';

interface PaymentItemMenu {
    paymentId: string;
    accountId: string;
    billId: string;
    serviceOrderId?: string;
}

export const PaymentItemMenu = ({ paymentId, accountId, serviceOrderId, billId }: PaymentItemMenu) => {
    const dispatch = useCommanderThunkDispatch();
    const realBillId = useCommanderSelector((s) => isBillVirtual(s, { id: billId })) ? undefined : billId;
    const resolveRoute = useResolveRoute();
    const enterpriseId = useCurrentEnterpriseId();
    const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();

    const { billAmounts } = useCommanderSelector(
        (state) =>
            getAmountDueForBillIds(state, {
                enterpriseId,
                billIds: [billId],
            }),
        isEqual
    );

    const [renderChangeReservationLinkDialog, toggleChangeReservationLinkDialog] = useDialogForm({
        title: lt('ChangeReservationLink'),
        renderForm: (props) => (
            <PaymentLinkReservationDialogContent
                {...props}
                paymentId={paymentId}
                accountId={accountId}
                serviceOrderId={serviceOrderId}
                onSuccess={handleFormSuccess(props.onSuccess)}
                billAmount={billAmounts[billId]}
            />
        ),
    });

    const handleFormSuccess = (onSuccess?: () => void) => () => {
        onSuccess?.();
        invalidatePaymentsTableRecordsQueries({ accountId });
        dispatch(
            refetchBill({
                accountId,
                billId: realBillId,
                extent: BILLING_EXTENT,
            })
        );
        dispatch(fetchLinkedReservationOwners({ billIds: [billId], accountId }));
    };

    const openDetailModal = () => dispatch(openModal(resolveRoute(RouteName.PaymentDetail, { id: paymentId })));

    const onViewReservationClick = () => {
        if (serviceOrderId) {
            const url = resolveRoute(RouteName.ReservationDetailStatus, { reservationId: serviceOrderId });
            dispatch(openModal(url));
        }
    };

    const reservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);

    return (
        <>
            <OrderItemDropdownButton icon={IconName.More} size="medium" variant="ghost">
                {serviceOrderId && reservationLinkToPaymentsEnabled ? (
                    <DropdownItem label={lt('ViewReservation')} onClick={onViewReservationClick} />
                ) : null}
                {reservationLinkToPaymentsEnabled ? (
                    <DropdownItem label={lt('ChangeReservationLink')} onClick={toggleChangeReservationLinkDialog} />
                ) : null}
                <DropdownItem label={lt('PaymentDetailPlural')} onClick={openDetailModal} />
            </OrderItemDropdownButton>
            {renderChangeReservationLinkDialog()}
        </>
    );
};
